/* eslint-disable */
import { renderToStaticMarkup } from 'react-dom/server';
import React from 'react';
import { STDoc } from '../engine/stationery-doc';
import { Ic } from '../engine/icons';
import { PBTpl } from '../engine/templatebuilder';
import { generateSectionsHTML } from './common';

// Import raw CSS files (Vite ?raw loader)
import docCss from '../engine/doc.css?raw';
import stationeryCss from '../engine/stationery.css?raw';
import stylesCss from '../engine/styles.css?raw';

/**
 * Compiles the V2 Editor State into a V1-compatible payload for the backend.
 * Supports both single-page (legacy sections[]) and multi-page (pages[]) formats.
 *
 * For multi-page: produces one <div class="pbd-page"> per page, each with the correct
 * stationery header/footer based on the repeat config (all / first).
 * Puppeteer renders each pbd-page as one physical PDF page via page-break-after:always.
 */
export const compileV2TemplateToV1Payload = (v2Payload: any, effTemplate: string) => {
  const { docId, docData, template, cols, metaFields, customTpl, style, frame, flags, name, stationerySel, stConfig } = v2Payload;

  // Dimensions for printing/rendering based on paper and orient
  const paper = v2Payload?.paper || v2Payload?.style?.paper || 'A4';
  const orient = v2Payload?.orient || v2Payload?.style?.orient || 'portrait';

  // Stationery config
  const m = (stConfig?.kind === 'preset' && stConfig.preset)
    ? { ...stConfig.preset, paper: paper || stConfig.preset.paper || 'A4', orient: orient || stConfig.preset.orient || 'portrait' }
    : null;

  const headerRepeat = m?.header?.repeat || 'all';
  const footerRepeat = m?.footer?.repeat || 'all';
  const compactCont = m?.header?.compactCont || false;

  // Helper: render header HTML for a given page index
  const renderHeaderForPage = (pageIdx: number): string => {
    if (!m || !m.header?.show) return '';
    const show = headerRepeat === 'all' || pageIdx === 0;
    if (!show) return '';
    const compact = compactCont && pageIdx > 0;
    return renderToStaticMarkup(<STDoc.Header m={m} compact={compact} ed={null} />);
  };

  // Helper: render footer HTML for a given page index
  const renderFooterForPage = (pageIdx: number, totalPages: number): string => {
    if (stConfig?.kind === 'preprinted' && stConfig.preset) {
      return pageIdx === 0
        ? `<div class="st-reserve st-reserve--bot" style="height: ${stConfig.preset.reserve?.bottom || 110}px"></div>`
        : '';
    }
    if (!m || !m.footer?.show) return '';
    const show = footerRepeat === 'all' || pageIdx === 0;
    if (!show) return '';
    return renderToStaticMarkup(<STDoc.Footer m={m} pageIndex={pageIdx} total={totalPages} compact={false} ed={null} />);
  };

  // Determine if this is a multi-page template
  const hasPages = v2Payload?.pages && Array.isArray(v2Payload.pages) && v2Payload.pages.length > 0;
  const pageList: { id: string; elements: any[] }[] = hasPages
    ? v2Payload.pages
    : [{ id: 'page-1', elements: v2Payload?.sections || [] }];

  const totalPages = pageList.length;

  // Construct the inline styles to apply document-level CSS variables
  const accent = style?.accent || '#1B2C6E';
  const logoH = (style?.logoSize || 24) + 'px';
  const isCanvas = true; // Always true for V2 canvas editor

  // Optimize V2 state payload (strip docData/content to reduce payload size)
  const optimizedPayload = {
    ...v2Payload,
    type: 'canvas',
    docData: undefined,
    // Save pages[] in new format; strip per-page element content for non-content types
    pages: pageList.map((pg: any) => ({
      id: pg.id,
      elements: (pg.elements || []).map((s: any) =>
        ['image', 'divider', 'table', 'buttons', 'signature', 'qr-code', 'bar-code'].includes(s.type)
          ? { ...s, content: '' }
          : s
      )
    })),
    // Keep legacy sections[] pointing to page 1 for backward compat readers
    sections: (pageList[0]?.elements || []).map((s: any) =>
      ['image', 'divider', 'table', 'buttons', 'signature', 'qr-code', 'bar-code'].includes(s.type)
        ? { ...s, content: '' }
        : s
    )
  };

  // Encode V2 state to Base64
  const base64State = btoa(unescape(encodeURIComponent(JSON.stringify(optimizedPayload))));

  // Dimensions
  let pW = paper === 'Letter' ? 816 : 794;
  let pH = paper === 'Letter' ? 1056 : 1123;
  if (orient === 'landscape') { const t = pW; pW = pH; pH = t; }

  // Watermark HTML
  const wm = v2Payload?.style?.watermark;
  const watermarkHtml = (wm?.show && wm?.image) ? `
    <div class="st-wm" style="position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; z-index: 0; pointer-events: none;">
      <img src="${wm.image}" alt="Watermark" style="opacity: ${(wm.opacity ?? 15) / 100}; width: ${wm.size ?? 50}%; transform: rotate(${wm.rotation ?? 0}deg); object-fit: contain; pointer-events: none; user-select: none;" />
    </div>
  ` : '';

  // Preprinted header (top reserve) — only page 1 gets the reserve block
  const preprintedHeaderHtml = (stConfig?.kind === 'preprinted' && stConfig.preset)
    ? `<div class="st-reserve st-reserve--top" style="height: ${stConfig.preset.reserve?.top || 150}px"></div>`
    : '';

  const stBrand = stConfig?.preset?.brand || '#2EB273';

  /**
   * Build the per-page HTML content.
   * If this is a legacy template (effTemplate used directly), wrap it in one page.
   */
  const buildPageHtml = (elements: any[], pageIdx: number): string => {
    const pageHtml = elements && elements.length > 0
      ? generateSectionsHTML(elements, 0, true)
      : effTemplate || '';

    let backendSafeTemplate = pageHtml
      .replace(/<span class="cx-ph-tag">([\s\S]*?)<\/span>/g, '$1')
      .replace(/\{\{\s*money\s+([^}]+)\}\}/g, '{{formatNumber $1 2}}')
      .replace(/\{\{\s*pct\s+([^}]+)\}\}/g, '{{$1}}')
      .replace(/\{\{\s*qty\s+([^}]+)\}\}/g, '{{$1}}')
      .replace(/\{\{\s*@number\s*\}\}/g, '{{inc @index}}')
      .replace(/\{\{\s*customer_data\s*\}\}/g, '{{#if customer}}{{customer}}{{else if customer_data.name}}{{customer_data.name}}{{else if customer_data.company_name}}{{customer_data.company_name}}{{else if customer_data.first_name}}{{customer_data.first_name}} {{customer_data.last_name}}{{else}}{{customer_data}}{{/if}}')
      .replace(/\{\{\s*([a-zA-Z0-9_]+)_data\s*\}\}/g, '{{#if $1}}{{$1}}{{else if $1_data.name}}{{$1_data.name}}{{else if $1_data.company_name}}{{$1_data.company_name}}{{else if $1_data.first_name}}{{$1_data.first_name}} {{$1_data.last_name}}{{else}}{{$1_data}}{{/if}}')
      .replace(/\{\{\s*payment_amount\s*\}\}/g, '{{#if payment_amount}}{{payment_amount}}{{else if amount_paid}}{{amount_paid}}{{else if total_gross_amount}}{{total_gross_amount}}{{else}}0.00{{/if}}')
      .replace(/\{\{\s*discount_amount\s*\}\}/g, '{{#if discount_amount}}{{discount_amount}}{{else if total_discount_amount}}{{total_discount_amount}}{{else}}0.00{{/if}}');

    if (pageIdx === 0) {
      // Only apply config block resolution on the first page (where effTemplate-based flags apply)
      backendSafeTemplate = PBTpl.resolveConfigBlocks(backendSafeTemplate, { ...flags, showLogo: style?.showLogo });
    }

    const hHtml = renderHeaderForPage(pageIdx);
    const fHtml = renderFooterForPage(pageIdx, totalPages);

    const isLastPage = pageIdx === totalPages - 1;

    return `
  <div class="pbd-page v2-page" data-page="${pageIdx + 1}" style="
    width: ${pW}px;
    min-width: ${pW}px;
    max-width: ${pW}px;
    height: ${pH}px;
    min-height: ${pH}px;
    position: relative;
    display: flex;
    flex-direction: column;
    background: white;
    overflow: hidden;
    ${isLastPage ? 'page-break-after: avoid; break-after: avoid;' : 'page-break-after: always; break-after: page;'}
  ">
    ${watermarkHtml}
    ${pageIdx === 0 && preprintedHeaderHtml ? preprintedHeaderHtml : ''}
    ${hHtml ? `<div class="st-head-wrap" style="width:100%;z-index:0;flex-shrink:0;pointer-events:none;">${hHtml}</div>` : ''}
    <div class="pbd-page-inner" data-density="${style?.density || 'normal'}" data-head="${style?.headStyle || 'standard'}" style="flex:1;padding:0;position:relative;z-index:1;overflow:hidden;">
      ${backendSafeTemplate}
    </div>
    ${fHtml ? `<div class="st-foot-wrap" style="width:100%;z-index:0;flex-shrink:0;pointer-events:none;">${fHtml}</div>` : ''}
  </div>`;
  };

  let compiledHtml = '';

  // If the effTemplate is a legacy V1 template (already contains <html>), preserve it with v2-state meta only
  const legacyBase = (v2Payload.sections && v2Payload.sections.length === 0 && !hasPages) ? effTemplate : '';
  if (legacyBase && legacyBase.toLowerCase().includes('<html')) {
    if (legacyBase.toLowerCase().includes('</head>')) {
      compiledHtml = legacyBase.replace(
        /<\/head>/i,
        `  <meta name="v2-state" content="${base64State}">\n</head>`
      );
    } else {
      compiledHtml = `<meta name="v2-state" content="${base64State}">\n${legacyBase}`;
    }
  } else {
    // Build the complete multi-page HTML document
    const pagesHtml = pageList
      .map((page: any, idx: number) => buildPageHtml(page.elements || [], idx))
      .join('\n');

    compiledHtml = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="v2-state" content="${base64State}">
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Noto+Kufi+Arabic:wght@400;500;600;700&family=Cairo:wght@400;600;700&display=swap');
    @import url('https://fonts.googleapis.com/css2?family=Alex+Brush&family=Allura&family=Dancing+Script:wght@400..700&family=Great+Vibes&family=Parisienne&family=Tangerine:wght@400;700&display=swap');
    
    /* Injected V2 Editor CSS */
    ${stylesCss}
    ${docCss}
    ${stationeryCss}

    /* Print-specific overrides */
    @page { 
      size: ${paper ? paper.toLowerCase() : 'a4'} ${orient || 'portrait'}; 
      margin: 0 !important; 
    }
    
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    body { 
      margin: 0; 
      padding: 0;
      -webkit-print-color-adjust: exact !important; 
      print-color-adjust: exact !important; 
    }

    /* Multi-page layout */
    .v2-pages {
      display: block;
    }

    /* Each .pbd-page is one physical PDF page */
    .v2-page {
      box-sizing: border-box;
    }

    @media print {
      html, body {
        width: ${pW}px !important;
        min-width: ${pW}px !important;
        max-width: ${pW}px !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      .v2-page {
        width: ${pW}px !important;
        height: ${pH - 1}px !important;
        min-height: ${pH - 1}px !important;
        max-height: ${pH - 1}px !important;
        margin: 0 !important;
        padding: 0 !important;
        page-break-inside: avoid !important;
      }
    }
    
    /* Canvas: no inner padding, elements are absolutely positioned */
    .pbd-page-inner { padding: 0 !important; }
    .st .pbd-page-inner { padding: 0 !important; }
    .pbd-page-inner p, .pbd-page-inner h1, .pbd-page-inner h2,
    .pbd-page-inner h3, .pbd-page-inner h4, .pbd-page-inner h5, .pbd-page-inner h6 {
      margin: 0 !important; padding: 0 !important;
      width: 100% !important; box-sizing: border-box !important;
    }
    .pbd-page-inner hr { width: 100% !important; margin: 0 !important; }
    .pbd-page-inner a { width: 100% !important; height: 100% !important; box-sizing: border-box !important; }
    .pbd-page-inner img { max-width: 100% !important; max-height: 100% !important; box-sizing: border-box !important; }
    .pbd-page-inner table {
      width: 100% !important; height: auto !important;
      min-width: 0 !important; /* Override doc.css min-width: max-content */
      border-collapse: collapse !important; white-space: normal !important;
      table-layout: fixed !important; margin: 0 !important;
      background-color: transparent !important;
    }
    .pbd-page-inner table tr { box-sizing: border-box !important; }
    .pbd-page-inner table td, .pbd-page-inner table th {
      box-sizing: border-box !important; overflow: hidden !important;
      text-overflow: ellipsis !important; vertical-align: middle !important;
    }
    .pbd-page-inner div { box-sizing: border-box !important; }

    .st-head, .st-foot {
      position: relative !important; overflow: hidden !important;
      background: transparent !important; padding: 0 !important;
      margin: 0 !important; border: none !important; box-sizing: border-box !important;
    }
    .st-head > *, .st-foot > * { box-sizing: border-box !important; }
    .pbd-page-inner, .st .pbd-page-inner { padding: 0 !important; margin: 0 !important; }

    /* Prevent table rows from splitting mid-row across pages */
    .pbd-items tr { page-break-inside: avoid; }
  </style>
</head>
<body>
  <div class="v2-pages ${stConfig?.kind === 'preset' ? 'st' : ''} ${['preset', 'preprinted', 'blank'].includes(stConfig?.kind) ? 'pb-stn' : ''}" style="--st-brand: ${stBrand}; --pbd-accent: ${accent}; --pbd-logo-h: ${logoH};">
    ${pagesHtml}
  </div>
</body>
</html>`.trim();
  }

  // Return the full payload structure expected by the existing backend.
  // sections: [] forces the backend to use raw `contents` HTML (V2 canvas mode).
  return {
    sections: [],
    contents: compiledHtml
  };
};
